/**
 * AudioPreview Component
 * Shows selected file info and upload button
 * Supports both audio and video files
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../Common/Button';

/**
 * Formats file size to human readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted size
 */
function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Checks if file is a video
 * @param {File} file - File object
 * @returns {boolean}
 */
function isVideoFile(file) {
  return file.type?.startsWith('video/') || 
    /\.(mp4|mov|avi|mkv|webm)$/i.test(file.name);
}

/**
 * Gets file icon based on type
 * @param {File} file - File object
 * @returns {React.ReactNode} Icon component
 */
function getFileIcon(file) {
  const isVideo = isVideoFile(file);
  
  if (isVideo) {
    return (
      <svg 
        xmlns="http://www.w3.org/2000/svg" 
        className="w-8 h-8 text-primary-600"
        viewBox="0 0 20 20" 
        fill="currentColor"
      >
        <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
      </svg>
    );
  }
  
  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      className="w-8 h-8 text-primary-600"
      viewBox="0 0 20 20" 
      fill="currentColor"
    >
      <path 
        fillRule="evenodd" 
        d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.972 7.972 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.983 5.983 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.414z" 
        clipRule="evenodd" 
      />
    </svg>
  );
}

/**
 * Audio/Video preview component
 * @param {Object} props - Component props
 * @param {File} props.file - Selected file
 * @param {Function} props.onUpload - Upload handler
 * @param {Function} props.onRemove - Remove handler
 * @param {boolean} props.isLoading - Loading state
 */
export function AudioPreview({ file, onUpload, onRemove, isLoading }) {
  const { t } = useTranslation(['upload', 'common']);
  
  const isVideo = useMemo(() => isVideoFile(file), [file]);
  
  const fileInfo = useMemo(() => ({
    name: file.name,
    size: formatFileSize(file.size),
    type: file.type || (isVideo ? 'video/unknown' : 'audio/unknown')
  }), [file, isVideo]);

  return (
    <div className="flex flex-col items-center justify-center py-8">
      {/* File card */}
      <div className="w-full max-w-md bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        {/* File info */}
        <div className="flex items-center gap-4 mb-6">
          {/* Icon */}
          <div className="w-16 h-16 bg-primary-50 rounded-xl flex items-center justify-center flex-shrink-0">
            {getFileIcon(file)}
          </div>

          {/* Details */}
          <div className="flex-1 min-w-0">
            <h4 
              className="text-lg font-semibold text-gray-900 truncate"
              title={fileInfo.name}
            >
              {fileInfo.name}
            </h4>
            <p className="text-sm text-gray-500 mt-1">
              {fileInfo.size} • {isVideo ? 'Video' : 'Audio'}
            </p>
          </div>
        </div>

        {/* Media preview */}
        <div className="mb-6">
          <p className="text-sm font-medium text-gray-700 mb-2">
            {t('preview.title')}:
          </p>
          {isVideo ? (
            <video 
              controls 
              className="w-full rounded-lg"
              src={URL.createObjectURL(file)}
              style={{ maxHeight: '200px' }}
            >
              {t('preview.videoNotSupported')}
            </video>
          ) : (
            <audio 
              controls 
              className="w-full"
              src={URL.createObjectURL(file)}
            >
              {t('preview.audioNotSupported')}
            </audio>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={onUpload}
            loading={isLoading}
            disabled={isLoading}
            variant="primary"
            className="flex-1"
          >
            {!isLoading && (
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="w-5 h-5 mr-2" 
                viewBox="0 0 20 20" 
                fill="currentColor"
              >
                <path 
                  fillRule="evenodd" 
                  d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" 
                  clipRule="evenodd" 
                />
              </svg>
            )}
            {t('preview.processButton')}
          </Button>

          <Button
            onClick={onRemove}
            disabled={isLoading}
            variant="secondary"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="w-5 h-5 mr-2" 
              viewBox="0 0 20 20" 
              fill="currentColor"
            >
              <path 
                fillRule="evenodd" 
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" 
                clipRule="evenodd" 
              />
            </svg>
            {t('common:actions.cancel')}
          </Button>
        </div>
      </div>

      {/* Info text */}
      <p className="mt-4 text-sm text-gray-500 text-center">
        {isVideo ? t('preview.videoInfoText') : t('preview.infoText')}
      </p>
    </div>
  );
}
